import { setUserIdInSentryScope } from "@utils/error/sentry";
import { loadServerContext, setCookiesFromMeristemContext } from "./context";
import { getEmailAndNameFromURLParams } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";

import getStore from "./redux/store";
import { ServerContextState } from "./redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
  expectUserData,
} from "./redux/slices/userData";
import { updateVisitorStatusState } from "./redux/slices/visitorStatus";
import getApiDomain from "src/utils/services/api-params";
import { trackEvent, updateUserContext } from "./api/tracker";

import visitTracker from "./services/VisitTracker";
import goto from "src/pageDefinitions/goto";
import { setInAppAutoCookie } from "./authCookieParser";
import {
  getCountryCode,
  getExperimentState,
  getLanguage,
  getMeristemContext,
  getSubdivision,
} from "./meristemContext";
import * as userSegments from "./userSegment";
import * as featureSegments from "./userSegment/features";
import { initConversionTracker } from "./services/ConversionTracker";
import { clearPromoCode } from "./redux/slices/promoCode";
import { triggerLoadPhase } from "src/vendor";
import { getConsent } from "./consent";
import { pendingScripts } from "./scriptLoader";
import { clearCheckoutTimerExpirationCookie } from "./checkoutTimer";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { identifyFullstoryUser } from "./monitoring/fullstory";
import { measureTask } from "./timing";
import { init as urlParamsPiiInit } from "./urlParamsPii";

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 */
export async function updateUserDataSlice(serverContext: ServerContextState) {
  const store = getStore();

  // Fetch user from backend if they are in the database.
  if (expectUserData()) {
    await store.dispatch(fetchAndInitializeExistingUser(serverContext.user_id));
  } else {
    await store.dispatch(initializeNewUser(serverContext));
  }
  const emailAndName = getEmailAndNameFromURLParams();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}

/**
 * Track changes to the user's visitor status to mixpanel
 *
 * 1. Add tracking observer to the visitorStatus slice in Redux.
 * 2. Track initial state.
 */
function trackVisitorState() {
  // Update visitor status state on initialization to kick off VisitorStateDetermined tracking
  // Only update visitor status state on landing and payment to avoid unnecessary duplicate
  // visitorStateDetermined tracking events on post checkout contexts
  if (userSegments.isPreSignup()) {
    getStore().dispatch(updateVisitorStatusState());
  }
}

function setInitialPeopleProperties() {
  const { userData } = getStore().getState();
  const properties: JsonObject = {
    country: getCountryCode(),
    subdivision: getSubdivision(),
    language: getLanguage(),
  };
  if (USER_IS_BOT) properties.userIsBot = true;
  updateUserContext(properties);
  identifyFullstoryUser(userData);
}

/**
 * Initialize redux, set up services, and fire off tracking calls.
 * @returns App initializes successfully
 */
export default async function initializeApplication() {
  // Expose debug tools
  (window as any).reduxState = getStore().getState;
  (window as any).grow = {
    getConsent,
    pendingScripts,
    setInAppAutoCookie,
    goto,
    userSegments,
    featureSegments,
    clearPromoCode: () => getStore().dispatch(clearPromoCode()),
    clearCheckoutTimerExpirationCookie,
    getSurveyAnswers,
  };

  // 1 - get the meristemContext data
  const meristemContext = getMeristemContext();
  setCookiesFromMeristemContext(meristemContext);

  // 2 - get serverContext from Django API
  const serverContext = await measureTask("serverContext", loadServerContext);

  // 5 - set up Sentry and attach user_id as a property
  setUserIdInSentryScope(serverContext.user_id);

  // 6 - Initialize visitTracker. This must happen before we fire any mixpanel events.
  visitTracker.init(getApiDomain());

  // 7 - update the UserData slice in redux (also, fetch userData if an existing user)
  await measureTask("userData", () => updateUserDataSlice(serverContext));

  // 8 - set up mixpanel tracker
  setInitialPeopleProperties();

  // 9 - maybe send an event that signals the user is allocated to an experiment
  const experimentsState = getExperimentState() || [];
  experimentsState.forEach((experimentState) => {
    trackEvent("OnMeristemExperimentDecided", {
      experimentName: experimentState.experimentName || null,
      variationName: experimentState.variationName || null,
      sha: experimentState.sha || experimentState.shaOverride || null,
    });
  });

  // 10 - track a visit
  visitTracker.createOrUpdateVisit();

  // 11 - set up a redux observer that updates visitor state
  trackVisitorState();

  initConversionTracker();

  // Before loading pixels, hide PII in the URL, since some pixels will send an event as soon as
  // they load which can include the current URL
  urlParamsPiiInit();

  triggerLoadPhase("after-context-init");
}
